// src/api/dynamicPricing.js
// FT-216 (динамическое ценообразование, инкременты A и B): единственная точка входа для
// /api/v1/dynamic_pricing/* и настроек машины. Компоненты ходят через эти обёртки,
// чтобы разбор кодов отказа и предикат «услуга в плане» не разъехались по местам.
//
// axios передаётся вызывающей стороной (this.axios) — тот же DI, что в api/rentToOwn.js:
// интерсептор авторизации уже висит на Vue.axios, а юнит-тесты подставляют ручной объект.

const BASE = "/api/v1/dynamic_pricing";

// REQ-15: услуга тарифного плана. Модуль открывает ТОЛЬКО ключ dynamic_pricing в
// plan.included_services — активный trial его не открывает сам по себе, пробный период
// даёт услугу, лишь когда она есть в плане. Это тот же образец, что rentToOwnInPlan, и
// НЕ общий includedInPlan компонентов (он для прочих услуг считает trial и не меняется).
export const DYNAMIC_PRICING_SERVICE = "dynamic_pricing";

function includedServices(mainCompany) {
  const plan = mainCompany && mainCompany.plan;
  return plan && Array.isArray(plan.included_services) ? plan.included_services : null;
}

// Пока план не загружен — услуги «нет» (как rentToOwnInPlan): создание скрыто, список и
// отмена остаются доступны, пояснение о тарифе показывается.
export function dynamicPricingInPlan(mainCompany) {
  const services = includedServices(mainCompany);
  return !!services && services.includes(DYNAMIC_PRICING_SERVICE);
}

// CTR-03, CTR-02, CTR-10: закрытый список кодов отказа → ключ локали. Карта литеральная
// (ключи не собираются из частей — dynamicKeys.spec.js). Тело ответа может нести код как
// { error: "code" }, { error: { code } } или { code } — разбираем все три формы.
const ERROR_KEYS = Object.freeze({
  starts_in_past: "dynamic_pricing.errors.starts_in_past",
  ends_before_start: "dynamic_pricing.errors.ends_before_start",
  car_not_participating: "dynamic_pricing.errors.car_not_participating",
  unknown_car: "dynamic_pricing.errors.unknown_car",
  unknown_price_group: "dynamic_pricing.errors.unknown_price_group",
  percent_out_of_range: "dynamic_pricing.errors.percent_out_of_range",
  empty_scope: "dynamic_pricing.errors.empty_scope",
  // область разрешилась в ноль участвующих машин (напр. «Весь филиал» без участвующих); empty_scope — «ничего не выбрано»
  no_participating_cars: "dynamic_pricing.errors.no_participating_cars",
  invalid_input: "dynamic_pricing.errors.invalid_input",
  module_not_in_plan: "dynamic_pricing.errors.module_not_in_plan",
  // 409 { error: { code: "car_lock_unavailable", reason: "car_lock_timeout" } } — машину держит другое изменение
  // цен; повторяемый отказ, не ошибка приложения: текст пользователю, без setError и Rollbar.
  car_lock_unavailable: "dynamic_pricing.errors.car_lock_unavailable",
  // Тот же отказ блокировки сырым кодом ShiftPrices (Operations::Base: 409 locked) — сервер без перевода в
  // car_lock_unavailable (сборка до холистического ревью FT-216): текст тот же.
  locked: "dynamic_pricing.errors.car_lock_unavailable",
  // Ролевые отказы ShiftPrices при применении/откате (Guards::Staff, Guards::AdminPlus) — тот же текст, что 403 без кода.
  staff_only: "dynamic_pricing.errors.forbidden",
  admin_plus_required: "dynamic_pricing.errors.forbidden",
  // 404 — поправки/машины нет в границе филиала (уже отменена, удалена или чужая): обработанный отказ.
  not_found: "dynamic_pricing.errors.not_found",
  // 422 — откат рекомендации позже 7 дней с применения (DynamicPricing::Rollback, окно ROLLBACK_WINDOW).
  rollback_expired: "dynamic_pricing.errors.rollback_expired",
});

export const DYNAMIC_PRICING_ERROR_CODES = Object.freeze(Object.keys(ERROR_KEYS));

export function dynamicPricingErrorCode(error) {
  const data = error && error.response && error.response.data;
  if (!data || typeof data !== "object") return null;
  if (typeof data.code === "string" && data.code) return data.code;
  if (typeof data.error === "string" && data.error) return data.error;
  if (data.error && typeof data.error === "object" && typeof data.error.code === "string") return data.error.code;
  return null;
}

// 403 `module_not_in_plan` — услуги нет в плане (REQ-15, CTR-10).
export function isModuleNotInPlan(error) {
  const status = error && error.response && error.response.status;
  return status === 403 && dynamicPricingErrorCode(error) === "module_not_in_plan";
}

// Разбор ошибки до setError: { code, status, i18nKey } для кодов из списка, иначе null —
// тогда вызывающая сторона отдаёт ошибку в setError (сеть, 5xx, незнакомый код).
// Ролевой 403 без кода тоже узнаём: у него свой текст.
export function parseDynamicPricingError(error) {
  const status = (error && error.response && error.response.status) || null;
  const code = dynamicPricingErrorCode(error);
  if (code && Object.prototype.hasOwnProperty.call(ERROR_KEYS, code)) {
    return { code, status, i18nKey: ERROR_KEYS[code] };
  }
  if (status === 403) return { code: "forbidden", status, i18nKey: "dynamic_pricing.errors.forbidden" };
  if (status === 404) return { code: "not_found", status, i18nKey: ERROR_KEYS.not_found };
  return null;
}

// Отказ действия (vm — компонент): код из списка — предупреждением с его текстом, прочее — в setError.
export function reportDynamicPricingError(vm, error, title) {
  const parsed = parseDynamicPricingError(error);
  if (parsed) {
    if (vm.$notify) vm.$notify(title, vm.$t(parsed.i18nKey), "warning", 6000);
  } else if (vm.setError) {
    vm.setError(error, title);
  }
}

// Действие над рекомендацией (применить, отклонить, не предлагать, откатить): отказ записи цен с незнакомым кодом
// (ShiftPrices: prices_required, invalid_mode, stale…) — обработанный отказ сервера, не сбой приложения: общий
// понятный текст без setError и Rollbar. Без кода, 5xx и сеть — как прежде, в setError.
export function parseRecommendationActionError(error) {
  const parsed = parseDynamicPricingError(error);
  if (parsed) return parsed;
  const status = (error && error.response && error.response.status) || null;
  const code = dynamicPricingErrorCode(error);
  if (code && (status === 422 || status === 409)) return { code, status, i18nKey: "dynamic_pricing.errors.action_refused" };
  return null;
}

export function reportRecommendationActionError(vm, error, title) {
  const parsed = parseRecommendationActionError(error);
  if (parsed) {
    if (vm.$notify) vm.$notify(title, vm.$t(parsed.i18nKey), "warning", 6000);
  } else if (vm.setError) {
    vm.setError(error, title);
  }
}

// Отказ загрузки: ожидаемые отказы (роль, услуга) — без Rollbar и тоста, блок сам объясняет; прочее — setError silent.
export function reportDynamicPricingLoadError(vm, error, title) {
  if (!parseDynamicPricingError(error) && vm.setError) vm.setError(error, title, { silent: true });
}

function unwrap(promise) {
  return promise.then((response) => response.data);
}

// CTR-03: список — массив поправок; на случай обёртки { date_adjustments | data }.
export function normalizeAdjustments(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.date_adjustments)) return data.date_adjustments;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}

// CTR-02: настройки машины из car_card_data или ответа PATCH → { participates, min_day_price, max_day_price };
// нет participates — участвует, нет границы — null (границы компании).
export function carDynamicPricingState(raw) {
  return {
    participates: raw.participates !== false,
    min_day_price: raw.min_day_price == null ? null : raw.min_day_price,
    max_day_price: raw.max_day_price == null ? null : raw.max_day_price,
  };
}

// ─── Инкремент B: настройки (CTR-01, CTR-10) и рекомендации (CTR-04, CTR-07) ─────────────────────────────
// Форма ответов сверена с бэкендом B (SettingsController#view, DynamicPricing::RecommendationsView,
// RecommendationsController): компоненты видят нормализованную форму, разбор полей — здесь и только здесь.

// REQ-01: значения по умолчанию — те же, что у колонок dynamic_pricing_settings.
export const SETTINGS_DEFAULTS = Object.freeze({
  enabled: false,
  no_response_mode: "show",
  no_response_days: 7,
  step_percent: 5,
  floor_percent: -20,
  ceiling_percent: 30,
  suggest_down: true,
  suggest_up: true,
});
export const NO_RESPONSE_MODES = Object.freeze(["show", "remind", "auto"]);
export const STEP_PERCENTS = Object.freeze([3, 5, 10]);
export const NO_RESPONSE_DAYS_RANGE = Object.freeze([1, 13]);
export const FLOOR_PERCENT_RANGE = Object.freeze([-90, 0]);
export const CEILING_PERCENT_RANGE = Object.freeze([0, 200]);
// Поля формы настроек, которые уходят в PATCH кнопкой «Сохранить» (suppressed_group_ids — отдельным действием).
export const SETTINGS_FORM_FIELDS = Object.freeze([
  "enabled", "no_response_mode", "no_response_days", "step_percent",
  "floor_percent", "ceiling_percent", "suggest_down", "suggest_up",
]);

function numberOr(value, fallback) {
  const n = Number(value);
  return value == null || value === "" || !Number.isFinite(n) ? fallback : n;
}

// CTR-01 (+ module_available CTR-10). Обёртку { settings } тоже принимаем. enabled_by — id включившего пользователя
// (число, SettingsController отдаёт enabled_by_id), enabled_at — момент включения.
export function normalizeSettings(raw) {
  const data = raw && raw.settings && typeof raw.settings === "object" ? raw.settings : raw || {};
  const d = SETTINGS_DEFAULTS;
  return {
    enabled: data.enabled === true,
    enabled_by: data.enabled_by == null ? null : data.enabled_by,
    enabled_at: data.enabled_at || null,
    no_response_mode: NO_RESPONSE_MODES.includes(data.no_response_mode) ? data.no_response_mode : d.no_response_mode,
    no_response_days: numberOr(data.no_response_days, d.no_response_days),
    step_percent: numberOr(data.step_percent, d.step_percent),
    floor_percent: numberOr(data.floor_percent, d.floor_percent),
    ceiling_percent: numberOr(data.ceiling_percent, d.ceiling_percent),
    suggest_down: data.suggest_down !== false,
    suggest_up: data.suggest_up !== false,
    suppressed_group_ids: Array.isArray(data.suppressed_group_ids) ? data.suppressed_group_ids.slice() : [],
    // нет поля (старый ответ) — считаем, что группы есть: подсказку «Собрать группы» показываем только по явному false
    groups_present: data.groups_present !== false,
    module_available: data.module_available == null ? null : data.module_available === true,
  };
}

// Вид рекомендации (REQ-07) и направление: kind price_change | date_adjustment, direction up | down; percent со знаком.
export const RECOMMENDATION_KINDS = Object.freeze({ PRICE_CHANGE: "price_change", DATE_ADJUSTMENT: "date_adjustment" });
// Применена (вручную или автоматически) — откатываемые статусы.
export const APPLIED_STATUSES = Object.freeze(["applied", "auto_applied"]);

function normalizeGroup(raw, fallbackId) {
  if (raw && typeof raw === "object") return { id: raw.id == null ? fallbackId : raw.id, min: raw.min, max: raw.max };
  return fallbackId == null ? null : { id: fallbackId, min: undefined, max: undefined };
}

// Машина рекомендации: «старая и новая цена» (REQ-10) — у открытой текущие значения, не снимок (REQ-08). Машина,
// которую применение сейчас исключило бы, приходит с excluded: true и new_price: null (старой может не быть, если у
// машины нет строки цены). У применённой поправки на даты обе цены null — машины без цен.
// У поправки на даты — ещё days [{ date, old_price, new_price, excluded }]: применение решает по каждому дню отдельно
// (машина занята 26-го, но свободна 27-го), поля машины — сводка для старого ответа. Нет days в ответе — нет и поля.
function normalizeCarDay(raw) {
  return {
    date: raw.date,
    old_price: numberOr(raw.old_price, null),
    new_price: numberOr(raw.new_price, null),
    excluded: raw.excluded === true,
  };
}

function normalizeCarDays(days) {
  return days.filter((d) => d && typeof d.date === "string").map(normalizeCarDay).sort((a, b) => a.date.localeCompare(b.date));
}

function normalizeCar(raw) {
  return {
    id: raw.id != null ? raw.id : raw.car_id,
    code: raw.code || raw.car_code || "",
    old_price: numberOr(raw.old_price, null),
    new_price: numberOr(raw.new_price, null),
    excluded: raw.excluded === true,
    ...(Array.isArray(raw.days) ? { days: normalizeCarDays(raw.days) } : {}),
  };
}

// Рекомендация CTR-04 (RecommendationsView#view): price_group { id, min, max } (и price_group_id), dates ["YYYY-MM-DD"]
// (только у поправки на даты), signals (доли 0…1: u, u_hat, window_f, window_f_hat; у поправки — days
// [{ date, f, f_hat }]), cars [{ id, code, old_price, new_price, excluded?, days? }], applied_at (UTC), application_kind manual | auto.
export function normalizeRecommendation(raw) {
  const r = raw || {};
  return {
    id: r.id,
    kind: r.kind === RECOMMENDATION_KINDS.DATE_ADJUSTMENT ? RECOMMENDATION_KINDS.DATE_ADJUSTMENT : RECOMMENDATION_KINDS.PRICE_CHANGE,
    direction: r.direction === "up" ? "up" : "down",
    percent: numberOr(r.percent, null),
    status: r.status || "open",
    application_kind: r.application_kind || null,
    group: normalizeGroup(r.price_group, r.price_group_id),
    dates: Array.isArray(r.dates) ? r.dates.slice().sort() : [],
    signals: r.signals && typeof r.signals === "object" ? r.signals : null,
    cars: Array.isArray(r.cars) ? r.cars.map(normalizeCar) : [],
    applied_at: r.applied_at || r.decided_at || null,
  };
}

// Ответ GET /recommendations и блок dynamic_pricing в dashboard_data (CTR-07) — форма RecommendationsView:
// { module_available, enabled, unit, next_check_at, open, applied (за 7 дней), insufficient_data [{ id, min, max }] }.
// Без услуги open и insufficient_data — пустые массивы (ключи есть). free_cars_upcoming (занятость FT-217) есть ТОЛЬКО в
// dashboard_data.dynamic_pricing и только при услуге и включённой фиче — GET /recommendations его не отдаёт, поэтому здесь
// его нет: виджет берёт занятость из своего prop data и перечитывание списка её не трогает.
// Поля, которых в ответе нет, остаются undefined — mergeRecommendations не затирает ими прежние.
export function normalizeRecommendations(raw) {
  const data = raw || {};
  const list = (key) => (Array.isArray(data[key]) ? data[key].map(normalizeRecommendation) : undefined);
  return {
    open: list("open"),
    applied: list("applied"),
    insufficient: Array.isArray(data.insufficient_data)
      ? data.insufficient_data.map((g) => normalizeGroup(g, g && g.price_group_id))
      : undefined,
    next_check_at: data.next_check_at,
    unit: data.unit,
    enabled: data.enabled == null ? undefined : data.enabled === true,
    module_available: data.module_available == null ? undefined : data.module_available === true,
  };
}

export function mergeRecommendations(prev, next) {
  const out = Object.assign({}, prev);
  Object.keys(next).forEach((key) => {
    if (next[key] !== undefined) out[key] = next[key];
  });
  return out;
}

// rollback → { status, skipped_rows: [{ price_id, car_id, reason }] }: reason changed | moved | out_of_bounds.
// Счёт по причинам в постоянном порядке; незнакомая причина — other. Ничего не пропущено — [].
export const ROLLBACK_SKIP_REASONS = Object.freeze(["changed", "moved", "out_of_bounds"]);
export function rollbackSkippedReasons(rows) {
  const counts = {};
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const reason = row && ROLLBACK_SKIP_REASONS.includes(row.reason) ? row.reason : "other";
    counts[reason] = (counts[reason] || 0) + 1;
  });
  return [...ROLLBACK_SKIP_REASONS, "other"].filter((r) => counts[r]).map((r) => [r, counts[r]]);
}

// apply → { status, applied_cars, excluded_cars, skipped_dates }: *_cars — массивы id (countOf принимает и число).
export function countOf(value) {
  if (Array.isArray(value)) return value.length;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export const dynamicPricingApi = {
  // GET → [{ id, starts_on, ends_on, percent, scope, price_group_ids, car_ids, source, created_at }]
  listAdjustments: (axios) => unwrap(axios.get(`${BASE}/date_adjustments`)).then(normalizeAdjustments),
  // POST { starts_on, ends_on, percent, scope, price_group_ids?, car_ids? } → созданная поправка + warnings
  createAdjustment: (axios, attrs) => unwrap(axios.post(`${BASE}/date_adjustments`, attrs)),
  // Предпросмотр: тот же POST с dry_run: true → { warnings: [] | ["out_of_car_bounds"] }, ничего не создаётся;
  // валидация (422) та же, что у создания.
  previewAdjustment: (axios, attrs) => unwrap(axios.post(`${BASE}/date_adjustments`, { ...attrs, dry_run: true })),
  // POST …/:id/revoke — поправки неизменяемы, правки нет (REQ-03).
  revokeAdjustment: (axios, id) => unwrap(axios.post(`${BASE}/date_adjustments/${id}/revoke`)),
  // CTR-02: PATCH /api/v1/cars/:id/dynamic_pricing { participates, min_day_price, max_day_price }
  updateCarSettings: (axios, carId, attrs) => unwrap(axios.patch(`/api/v1/cars/${carId}/dynamic_pricing`, attrs)),
  // CTR-01: GET/PATCH /settings, тело PATCH — плоское { enabled, … } (как PATCH { enabled: false } в плане STEP-17).
  getSettings: (axios) => unwrap(axios.get(`${BASE}/settings`)).then(normalizeSettings),
  updateSettings: (axios, attrs) => unwrap(axios.patch(`${BASE}/settings`, attrs)).then(normalizeSettings),
  // CTR-04
  listRecommendations: (axios) => unwrap(axios.get(`${BASE}/recommendations`)).then(normalizeRecommendations),
  applyRecommendation: (axios, id) => unwrap(axios.post(`${BASE}/recommendations/${id}/apply`)),
  rejectRecommendation: (axios, id) => unwrap(axios.post(`${BASE}/recommendations/${id}/reject`)),
  suppressRecommendation: (axios, id) => unwrap(axios.post(`${BASE}/recommendations/${id}/suppress`)),
  // suppress → { status, suppressed_group_ids }; rollback → { status, skipped_rows }; reject → { status }.
  // Не open — ответ текущего статуса (повтор, уже решена другим), не ошибка.
  rollbackRecommendation: (axios, id) => unwrap(axios.post(`${BASE}/recommendations/${id}/rollback`)),
  // Первый показ (REQ-12, план STEP-16): виджет после отрисовки шлёт id показанных открытых.
  markShown: (axios, ids) => unwrap(axios.post(`${BASE}/recommendations/shown`, { ids })),
};
